import type { ImgHTMLAttributes } from 'react';

import { useAuthorizedFileUrl } from '@/hooks/useAuthorizedFileUrl';

export type AuthorizedImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src?: null | string;
  /** Rendered while a private link is still resolving, and if it never does. */
  fallbackSrc?: null | string;
};

/**
 * Drop-in `<img>` for anything that may be a backend file link.
 *
 * Private links (`…/api/v1/<service>/…`) are fetched through axios and shown as
 * a blob: URL; bundled assets and local `blob:` previews pass straight through,
 * so this is safe wherever the source can be either. Renders nothing when there
 * is no image to show and no fallback.
 */
export const AuthorizedImage = ({ src, fallbackSrc, ...imgProps }: AuthorizedImageProps) => {
  const { url } = useAuthorizedFileUrl(src);
  const resolved = url ?? fallbackSrc;

  if (!resolved) return null;

  return <img {...imgProps} src={resolved} />;
};
